/**
 * @fileoverview Client-side reload button island for server error boundaries.
 * @description A tiny client component that provides interactive functionality
 * (onClick handler) for server-rendered error boundaries without violating RSC boundaries.
 * Includes comprehensive error handling and user feedback.
 * 
 * @module components/blog/features/ErrorBoundaries/ReloadButtonIsland
 */

'use client'

import { useState, useCallback } from 'react'

import { sendEvent } from '~/lib/gtag'
import { logClientError } from '~/lib/logger-client'

/**
 * Props for the ReloadButtonIsland component
 */
interface ReloadButtonIslandProps {
  /** Optional error ID for tracking reload attempts */
  errorId?: string
  /** Optional topic context for logging */
  topic?: string
  /** Custom button text */
  buttonText?: string
  /** Whether to show loading state during reload */
  showLoadingState?: boolean
}

/**
 * State for the reload button
 */
interface ReloadButtonState {
  /** Whether the reload is in progress */
  isLoading: boolean
  /** Whether the reload failed */
  hasFailed: boolean
  /** Error message if reload failed */
  errorMessage?: string
}

/**
 * Logs reload attempts for monitoring and debugging
 * 
 * @param errorId - Optional error ID for tracking
 * @param topic - Optional topic context
 * @param success - Whether the reload was successful
 * @param error - Error object if reload failed
 */
function logReloadAttempt(
  errorId?: string, 
  topic?: string, 
  success = true, 
  error?: Error
): void {
  const logData = {
    action: 'reload_attempt',
    success,
    timestamp: new Date().toISOString(),
    errorId,
    topic,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
  }

  if (process.env.NODE_ENV === 'development') {
    console.group('🔄 Reload Button Action')
    console.log('Reload attempt:', logData)
    if (error) {
      console.error('Reload error:', error)
    }
    console.groupEnd()
  } else if (error) {
    // In production, route a failed reload through the client drain. The
    // attempt itself is already reported as the analytics event below, so the
    // success case needs no separate log line.
    logClientError(error, {
      component: 'ReloadButtonIsland',
      context: 'Reload button error',
      errorId,
      topic,
    })
  }

  // Report the reload attempt as a registered analytics event.
  sendEvent({
    action: 'reload_attempt',
    params: {
      success,
      ...(errorId ? { error_id: errorId } : {}),
      ...(topic ? { topic } : {}),
    },
  })
}

/**
 * Tiny client island for reload functionality
 * This component provides the interactive "Try Again" button that server
 * components cannot render due to RSC boundary restrictions.
 * 
 * Features:
 * - Loading state during reload
 * - Error handling if reload fails
 * - Comprehensive logging for monitoring
 * - Accessibility features
 * - Graceful fallback if window is not available
 * 
 * @param props - Component props
 * @returns JSX element containing the reload button
 */
export default function ReloadButtonIsland({
  errorId,
  topic,
  buttonText = 'Try Again',
  showLoadingState = true
}: ReloadButtonIslandProps): JSX.Element {
  const [state, setState] = useState<ReloadButtonState>({
    isLoading: false,
    hasFailed: false,
    errorMessage: undefined
  })

  /**
   * Handles the reload action with comprehensive error handling
   * This function is synchronous because window.location.reload() is synchronous
   * and doesn't return a promise that needs to be awaited.
   */
  const handleReload = useCallback((): void => {
    try {
      setState(prev => ({ ...prev, isLoading: true, hasFailed: false, errorMessage: undefined }))
      
      // Check if window is available (client-side only)
      if (typeof window === 'undefined') {
        throw new Error('Window object not available - this component must run on the client')
      }

      // Log the reload attempt
      logReloadAttempt(errorId, topic, true)

      // Perform the reload - this is synchronous and will navigate away immediately
      // No need to await or handle promises since the page will reload
      window.location.reload()
      
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown reload error')
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        hasFailed: true,
        errorMessage: errorObj.message
      }))

      // Log the failed reload attempt
      logReloadAttempt(errorId, topic, false, errorObj)

      // In development, show more detailed error information
      if (process.env.NODE_ENV === 'development') {
        console.error('Reload button failed:', errorObj)
      }
    }
  }, [errorId, topic])

  /**
   * Handles keyboard events for accessibility
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleReload()
    }
  }, [handleReload])

  // If window is not available (SSR), render a disabled button
  // This ensures the component works properly when server-side rendered
  if (typeof window === 'undefined') {
    return (
      <button
        disabled
        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-400 cursor-not-allowed dark:border-gray-600 dark:text-gray-500"
        aria-label="Reload functionality not available on server"
        title="This button requires JavaScript to function"
      >
        {buttonText}
      </button>
    )
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        onClick={handleReload}
        onKeyDown={handleKeyDown}
        disabled={state.isLoading}
        className={`
          rounded-lg border px-4 py-2 text-sm font-semibold transition-colors
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${state.isLoading 
            ? 'border-gray-300 text-gray-400 cursor-not-allowed dark:border-gray-600 dark:text-gray-500' 
            : state.hasFailed
            ? 'border-red-300 text-red-700 hover:bg-red-50 focus:ring-red-500 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20'
            : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
          }
        `}
        aria-label={state.isLoading ? 'Reloading page...' : `Reload page to try again${state.hasFailed ? ' (previous attempt failed)' : ''}`}
        title={state.isLoading ? 'Please wait while the page reloads' : 'Click to reload the page and try again'}
      >
        {state.isLoading && showLoadingState ? (
          <span className="flex items-center gap-2">
            <svg 
              className="h-4 w-4 animate-spin" 
              fill="none" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle 
                className="opacity-25" 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
              />
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Reloading...
          </span>
        ) : (
          buttonText
        )}
      </button>

      {/* Error message display */}
      {state.hasFailed && state.errorMessage && (
        <p className="text-xs text-red-600 dark:text-red-400 text-center max-w-xs">
          Reload failed: {state.errorMessage}
        </p>
      )}

      {/* Additional help text for failed reloads */}
      {state.hasFailed && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-xs">
          You can also try refreshing your browser manually or navigating away and back.
        </p>
      )}
    </div>
  )
}
